import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { itemsNotAlreadySaved, findListByName } from "@/lib/todoSaveIdempotency";
import { ensureDefaultTodoList } from "@/lib/defaultTodoList";
import { OPEN_TODO_LIST_KEY, recordSavedList } from "@/lib/savedListLink";
import { stripModalTags } from "@/lib/modalTags";
// #1900 PR 3 — the hidden routine draft. Imported from the one module that
// owns the regex, so the render, the persist and the SMS path cannot disagree.
import { stripRoutineDraft } from "../../supabase/functions/_shared/routines";
import { stripMealPlanRunTag } from "@/lib/mealPlanChatContext";
import { Copy, Check } from "lucide-react";
import KinAvatar from "./KinAvatar";
import TypingIndicator from "./TypingIndicator";
import ImageGrid, { type ImageResult } from "./ImageGrid";
import KrogerPricing, { hasGroceryList, parseGroceryItems, isCategoryHeader, containsNonFoodKeyword } from "./KrogerPricing";
import MessageFeedback from "./MessageFeedback";
import SchedulePreview, { type ScheduleEvent } from "./SchedulePreview";
import { useAuth } from "@/hooks/useAuth";
import { SIGNUP_FILING_PHRASES, SIGNUP_PRO_LINE, SIGNUP_READING_PHRASES, signupRowChipLabel } from "../../supabase/functions/_shared/signupSheet";
import { ProUpsellLink } from "@/components/ProUpsell";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { openMyKinTo, type MyKinIntent } from "@/lib/myKinEvents";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { openExternal } from "@/lib/openExternal";
import {
  detectMealPlanType,
  stripMealPlanTags,
  parseMealPlanDays,
  parseRecipe,
  buildWeekOfTitle,
  cleanRecipeContent,
  cleanMealPlanContent,
} from "@/lib/mealPlanParser";
import { attachPlanProteinGrams, parsePlanProteinBlock } from "@/lib/mealPlanProteinGrams";
import { isSilentLearnKey, naturalPhrase } from "@/lib/familyFactPhrase";
import { trackFeature } from "@/lib/analytics";
import { logActivity } from "@/lib/activityLog";

/**
 * A family fact the SERVER confirmed it wrote, with its row id. #915.
 *
 * The chip used to be built by parsing the [LEARN] tag out of the message text.
 * Two things were wrong with that: the tag carries no row id, so Undo had to
 * match on description and archived every sibling of a multi-valued key (#747);
 * and message text is the same channel the model writes, which is exactly what
 * #890 STEP 1 set out to stop being an input.
 */
export interface LearnReceipt {
  id: string;
  category: string;
  subject: string | null;
  key: string;
  value: string;
  updated: boolean;
}

export interface Message {
  /**
   * #1882 — the `messages` row id, when one is known.
   *
   * ⚠️ OPTIONAL ON PURPOSE, and every consumer must handle its absence rather
   * than assume a message can be addressed. Three legitimate ways a rendered
   * message has none: it is still streaming (the row is not written until the
   * stream finishes); its insert was not proved to have landed (house rule 16
   * — the id is taken back off, see `clearMessageId`); or it is a local-only
   * bubble that was never persisted at all, like the "Something glitched on my
   * end" error reply or a limit-block notice.
   *
   * Minted client-side so the client holds it without a round-trip. See
   * `src/lib/chatMessageIdentity.ts` for the select/map pair that carries it
   * back on a reload.
   */
  id?: string;
  /**
   * #1877 — the `todo_lists` row this message's save created, when it created
   * one. Durable: it comes back from `messages.saved_list_id` on a reload,
   * which is what makes the "Saved ✓" pill a link instead of a dead badge.
   *
   * ⚠️ Null/absent is the NORMAL case and means three different things, none of
   * them an error: the message never saved anything; it saved to the general
   * To-Dos destination, which has no `todo_lists` row to point at; or she has
   * since deleted the list and the FK's `on delete set null` cleared it. All
   * three must read as "no link to offer", never as "link lost".
   */
  savedListId?: string | null;
  role: "user" | "assistant";
  content: string;
  /**
   * Server-authored receipts for facts learned on this turn.
   *
   * ⚠️ Live-session only, and that is not a regression: the [LEARN] tag is
   * STRIPPED before the message is persisted (Index.tsx, the `.replace(/\[LEARN\]…/)`
   * on the assistant insert), so chips never survived a reload under the old
   * scheme either. Believing otherwise is what kept this migration parked on
   * #890.
   */
  learnReceipts?: LearnReceipt[];
  images?: ImageResult[];
  userImage?: string; // data URL for user-uploaded photo (first image; back-compat)
  userImages?: string[]; // data URLs for all user-uploaded photos on this message
  /**
   * #1896 — "which row is yours?" on a snapped sign-up sheet.
   *
   * ⚠️ LIVE-SESSION ONLY, like `learnReceipts`, and deliberately so. These are
   * a question, not a record: once she has answered, the answer is the filed
   * rows on Today and in Tasks, and re-rendering the question on a reload would
   * invite her to file the same sheet twice. The function dedupes that anyway,
   * but offering it is still the wrong thing to show.
   */
  signupPicker?: SignupPicker;
  /**
   * The sign-up sheet's in-flight placeholder, replaced in place by the
   * answer. Rendered as the animated thinking bubble, never as a static
   * message (Nov 2 2026: a static one looked frozen). Never persisted.
   */
  signupPending?: "reading" | "filing";
  /** Which sign-up request this placeholder belongs to. Settled by this id, never by position. */
  signupReqId?: number;
  /**
   * The sign-up flow ended without filing (unreadable, nothing landed, or a
   * picker still open). A photo she sends next goes back to the sign-up
   * reader instead of to a fresh chat turn that has no idea what it is.
   */
  signupRetry?: boolean;
  /**
   * #1998 — the sign-up reply ends on the free-tier Pro line, and the function
   * said so (`pro_upsell`). That line renders as a tap through to Kin Pro.
   * Live-session only; a reloaded message shows the same words as plain text.
   */
  signupProUpsell?: boolean;
}

/** #1896 — the rows Kin found, for her to pick from. */
export interface SignupPicker {
  sheetTitle: string;
  rows: Array<{ index: number; item: string; person_text: string }>;
  /** Rows Kin thinks are hers (her name, a kid's name, or her family name). */
  matchedRows: number[];
  /** True once she has answered, so the block stops accepting taps. */
  answered?: boolean;
  /**
   * The whole first-pass read, sent back with her choice so filing never
   * re-reads the photo (a second read numbers the rows differently).
   */
  sheet?: unknown;
}

interface ChatMessagesProps {
  messages: Message[];
  isLoading: boolean;
  selectedImageUrl?: string | null;
  onImageSelect?: (image: ImageResult | null) => void;
  onAskAboutPhoto?: () => void;
  onOpenRenoTracker?: () => void;
  onSendMessage?: (msg: string) => void;
  /** #1896 — she tapped Done on a sign-up sheet picker. */
  onSignupRowsChosen?: (messageIndex: number, rowIndexes: number[]) => void;
  keyboardOpen?: boolean;
}

const SCHEDULE_RE = /\[SCHEDULE_JSON\]([\s\S]*?)\[\/SCHEDULE_JSON\]/;

function parseScheduleJSON(content: string): ScheduleEvent[] | null {
  const match = content.match(SCHEDULE_RE);
  if (!match) return null;
  try {
    const parsed = JSON.parse(match[1].trim());
    if (Array.isArray(parsed)) return parsed;
  } catch { /* ignore */ }
  return null;
}

function stripScheduleJSON(content: string): string {
  return content.replace(SCHEDULE_RE, "").trim();
}

const URL_RE = /(https?:\/\/[^\s]+)/g;

const renderTextRun = (text: string, keyPrefix: string) => {
  const segments = text.split(URL_RE);
  return segments.map((seg, k) => {
    if (/^https?:\/\//.test(seg)) {
      const trailingMatch = seg.match(/[).,!?;:]+$/);
      const trailing = trailingMatch ? trailingMatch[0] : "";
      const href = trailing ? seg.slice(0, -trailing.length) : seg;
      return (
        <span key={keyPrefix + "-u" + k}>
          <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => { e.preventDefault(); openExternal(href); }} className="underline break-all" style={{ color: "#5050C8" }}>{href}</a>
          {trailing}
        </span>
      );
    }
    return seg.split("\n").map((line, j, arr) => (
      <span key={keyPrefix + "-" + k + "-" + j}>
        {line}
        {j < arr.length - 1 && <br />}
      </span>
    ));
  });
};

/** Render bold **text** as <strong> with periwinkle color, and linkify URLs. */
const renderContent = (text: string) => {
  text = text.replace(/^#{1,6}\s+/gm, "");
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return (
        <strong key={i} className="font-semibold text-kin-periwinkle">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return renderTextRun(part, String(i));
  });
};

const CopyButton = ({ text, label }: { text: string; label?: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 rounded-full transition-colors flex-shrink-0 whitespace-nowrap border"
      style={{
        fontSize: "clamp(12px, 2.8vw, 13px)",
        padding: "6px 10px",
        backgroundColor: copied ? "transparent" : "#FFFFFF",
        color: copied ? "#22c55e" : "#5050C8",
        borderColor: copied ? "#22c55e" : "#5050C8",
      }}
    >
      {!copied && <Copy size={12} />}
      <span>{copied ? "Copied ✓" : (label || "Copy")}</span>
    </button>
  );
};

/**
 * Per-message link state. When the user saves a meal plan + grocery list from
 * the same response, we link them in both directions.
 */
interface LinkRef {
  mealPlanId?: string;
  groceryListId?: string;
}

const PillButton = ({
  onClick,
  disabled,
  saved,
  label,
  savingLabel,
  savedLabel,
  savedClickable = false,
}: {
  onClick: () => void;
  disabled?: boolean;
  saved: boolean;
  label: string;
  savingLabel: string;
  savedLabel: string;
  /**
   * #1877 — keep the button live once `saved` is true.
   *
   * ⚠️ DEFAULTS FALSE so every existing caller is byte-identical. `saved` has
   * always implied `disabled` here, which is right for a one-shot save: it is
   * what stops a second tap re-running the write. #1877's linked pill is the
   * one case where a saved button still has somewhere to go — it navigates and
   * writes nothing — and without this it would render with an onClick that can
   * never fire, which is the quietest possible way for the feature to not work.
   */
  savedClickable?: boolean;
}) => (
  <button
    onClick={onClick}
    disabled={disabled || (saved && !savedClickable)}
    className="flex items-center gap-1 rounded-full transition-colors flex-shrink-0 whitespace-nowrap border"
    style={{
      fontSize: "clamp(12px, 2.8vw, 13px)",
      padding: "6px 10px",
      backgroundColor: "transparent",
      color: saved ? "#22c55e" : "#5050C8",
      borderColor: saved ? "#22c55e" : "#5050C8",
      opacity: disabled && !saved ? 0.7 : 1,
    }}
  >
    <span>{saved ? savedLabel : (disabled ? savingLabel : label)}</span>
  </button>
);

/**
 * Destination copy for the "Saved to My Kin" confirmation toast. Warm, no
 * jargon, Kin speaks as "she". Matches the Add-a-Recipe toast tone in MyKinTab.
 */
type SaveDestination = "recipe" | "meal_plan" | "grocery" | "todos";

const SAVE_DESTINATION_COPY: Record<SaveDestination, string> = {
  recipe: "Find it in Recipes & Meals",
  meal_plan: "Find it in Meal Plans",
  grocery: "It's in your Grocery Lists",
  todos: "They're in Notes & To-Dos",
};

/**
 * Fire the "Saved to My Kin 💕" confirmation toast naming the destination, with
 * a [View it] action that lands on the saved item in My Kin. Fires on EVERY
 * successful save (not just the first) — the inline "Saved ✓" button state is
 * unaffected. Feedback layer only: nothing about what/how gets saved changes.
 */
function showSavedToMyKinToast(dest: SaveDestination, intent: MyKinIntent) {
  toast.success("Saved to My Kin 💕", {
    description: SAVE_DESTINATION_COPY[dest],
    action: {
      label: "View it",
      onClick: () => openMyKinTo(intent),
    },
  });
}

const SaveToMyKinButton = ({
  messageContent,
  rawContent,
  mealType,
  hasGrocery,
  linkRef,
  onSavedGroceryList,
}: {
  messageContent: string;
  rawContent: string;
  mealType: "meal_plan" | "recipe" | null;
  hasGrocery: boolean;
  linkRef: React.MutableRefObject<LinkRef>;
  onSavedGroceryList?: (listId: string) => void;
}) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  const saveGroceryAsListLinkedTo = async (mealPlanId: string) => {
    if (!user) return;
    const items = parseGroceryItems(messageContent)
      .filter((item) => !isCategoryHeader(item))
      .filter((item) => !containsNonFoodKeyword(item));
    const title = buildWeekOfTitle();
    const { data: list, error: listErr } = await supabase
      .from("shopping_lists")
      .insert({
        user_id: user.id,
        name: title,
        source_meal_plan_id: mealPlanId,
      })
      .select("id")
      .single();

    if (listErr || !list) return;
    linkRef.current.groceryListId = list.id;
    if (items.length > 0) {
      // #760. The list row above is checked; this was not, so a refused items
      // insert produced a grocery list with a NAME and NOTHING IN IT — and
      // handleSave fires showSavedToMyKinToast straight after, telling her it
      // saved. A false confirmation, not a silent loss (house rule 16).
      //
      // Throws rather than returning: handleSave awaits this, and the throw is
      // what suppresses its toast. Returning quietly would leave the
      // confirmation intact, which is the whole defect.
      const { error: itemsErr } = await supabase.from("shopping_list_items").insert(
        items.map((item) => ({
          user_id: user.id,
          list_id: list.id,
          item,
          checked: false,
        }))
      );
      if (itemsErr) throw itemsErr;
    }
    // The backlink is what makes "View grocery list" appear on the meal-plan
    // card. Best-effort and deliberately NOT thrown on: the list and its items
    // both exist by now, so failing the whole save over a missing shortcut
    // would throw away work that really landed.
    const { error: linkErr } = await supabase
      .from("meal_plans")
      .update({ grocery_list_id: list.id })
      .eq("id", mealPlanId);
    if (linkErr) console.warn("[Chat] meal-plan grocery backlink failed", linkErr);
    onSavedGroceryList?.(list.id);
  };

  /**
   * Save the response's grocery list as a shopping list linked to a recipe.
   * Title is JUST the recipe name (no date suffix). No-ops if there are no
   * parseable items so we never persist an empty list.
   */
  const saveGroceryAsListLinkedToRecipe = async (recipeId: string, recipeTitle: string) => {
    if (!user) return;
    const items = parseGroceryItems(messageContent)
      .filter((item) => !isCategoryHeader(item))
      .filter((item) => !containsNonFoodKeyword(item));
    if (items.length === 0) return;
    const title = (recipeTitle || "").trim() || "Recipe Grocery List";
    const { data: list, error: listErr } = await supabase
      .from("shopping_lists")
      .insert({
        user_id: user.id,
        name: title,
        source_recipe_id: recipeId,
      } as any)
      .select("id")
      .single();
    if (listErr || !list) return;
    linkRef.current.groceryListId = list.id;
    // #760 — same as the meal-plan path above. handleSave calls this and then
    // fires showSavedToMyKinToast, so an unread failure here tells her the
    // recipe's grocery list saved when it is empty. Throw so that toast is
    // suppressed.
    const { error: itemsErr } = await supabase.from("shopping_list_items").insert(
      items.map((item) => ({
        user_id: user.id,
        list_id: list.id,
        item,
        checked: false,
      }))
    );
    if (itemsErr) throw itemsErr;
    onSavedGroceryList?.(list.id);
  };

  const saveGroceryToDefault = async () => {
    if (!user) return;
    const items = parseGroceryItems(messageContent)
      .filter((item) => !isCategoryHeader(item))
      .filter((item) => !containsNonFoodKeyword(item));
    const { data: existing } = await supabase
      .from("grocery_list")
      .select("item")
      .eq("user_id", user.id);
    const existingSet = new Set((existing || []).map((e: any) => e.item.toLowerCase()));
    const newItems = items.filter((i) => !existingSet.has(i.toLowerCase()));
    if (newItems.length > 0) {
      // #760. Same shape, different table — this is the "add to my main
      // grocery list" path, and its caller confirms too.
      const { error: insErr } = await supabase.from("grocery_list").insert(
        newItems.map((item) => ({ user_id: user.id, item, checked: false }))
      );
      if (insErr) throw insErr;
    }
  };

  const handleSave = async () => {
    if (!user || saving || saved) return;
    setSaving(true);

    try {
      if (mealType === "recipe") {
        const cleanContent = stripMealPlanTags(rawContent);
        const structured = cleanRecipeContent(cleanContent);
        const { title, description } = parseRecipe(cleanContent);
        const { data: recipeRow, error } = await supabase
          .from("saved_recipes")
          .insert({
            user_id: user.id,
            title,
            description,
            full_text: structured,
          })
          .select("id")
          .single();
        if (error || !recipeRow) {
          toast.error("Couldn't save the recipe. Try again?");
          return;
        }
        // If the same response also contains a grocery list, save it as a
        // shopping list titled with just the recipe name and link both ways.
        if (hasGrocery) {
          await saveGroceryAsListLinkedToRecipe(recipeRow.id, title);
        }
        setSaved(true);
        trackFeature("recipe_saved", { has_grocery: hasGrocery });
        logActivity("recipe_save", { source: "chat", has_grocery: hasGrocery });
        if (hasGrocery) trackFeature("grocery_list_created", { source: "recipe" });
        showSavedToMyKinToast("recipe", {
          section: "recipes",
          highlight: { kind: "recipe", id: recipeRow.id },
        });
        return;
      }

      if (mealType === "meal_plan") {
        // Save meal plan first.
        // Pass RAW content to cleanMealPlanContent so it can use [GROCERY_LIST]
        // as the meal-plan/grocery boundary before the tags get stripped.
        const structured = cleanMealPlanContent(rawContent);
        // #1789 — read the hidden protein block off the RAW response, before
        // anything strips it. `cleanMealPlanContent` removes the block (it must
        // not reach `raw_markdown`, which feeds grocery generation), so parsing
        // from `structured` would always find nothing.
        //
        // ⚠️ Matched by day + meal NAME inside attachPlanProteinGrams, never by
        // position — see that function for why a positional zip silently files
        // a real-looking number against the wrong night.
        const proteinEntries = parsePlanProteinBlock(rawContent);
        const days = attachPlanProteinGrams(parseMealPlanDays(structured), proteinEntries);
        const title = buildWeekOfTitle();
        const { data: plan, error: planErr } = await supabase
          .from("meal_plans")
          .insert({
            user_id: user.id,
            title,
            meals_json: days as any,
            raw_markdown: structured,
          })
          .select("id")
          .single();

        if (planErr || !plan) {
          toast.error("Couldn't save your meal plan. Try again?");
          return;
        }
        linkRef.current.mealPlanId = plan.id;

        // If there's a grocery list in the same response, save + link it
        if (hasGrocery) {
          await saveGroceryAsListLinkedTo(plan.id);
        }
        setSaved(true);
        trackFeature("meal_plan_saved", { has_grocery: hasGrocery });
        logActivity("meal_plan", { source: "chat", has_grocery: hasGrocery });
        if (hasGrocery) trackFeature("grocery_list_created", { source: "meal_plan" });
        showSavedToMyKinToast("meal_plan", {
          section: "recipes",
          highlight: { kind: "meal_plan", id: plan.id },
        });
        return;
      }

      // Plain grocery list
      if (hasGrocery) {
        await saveGroceryToDefault();
        setSaved(true);
        trackFeature("grocery_list_created", { source: "chat" });
        showSavedToMyKinToast("grocery", { section: "grocery" });
      }
    } catch (err) {
      // #760. The ONLY change to handleSave itself, and it is required rather
      // than tidy-up: this block had a `finally` and no `catch`, so the grocery
      // helpers' new throws would have become unhandled promise rejections —
      // she would see no "Saved ✓" and no explanation either, and Sentry would
      // fill with noise. Failure path only; every success path above is
      // untouched, and no parsing, filtering or tag detection is changed.
      console.error("[Chat] save failed", err);
      toast.error("Couldn't save that to My Kin. Mind trying again?");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PillButton
      onClick={handleSave}
      disabled={saving}
      saved={saved}
      label="💜 Save to My Kin"
      savingLabel="Tucking it away..."
      savedLabel="Saved ✓"
    />
  );
};

const BUDGET_BUTTON_TAG = "[BUDGET_TRACKER_BUTTON]";
// #680 extends this tag with an optional name="..." attribute so a themed
// to-do list (a back-to-school checklist, a trip-packing list) can save as
// its own named list instead of dissolving into loose general-list rows.
// Backward compatible by construction: the new group is optional, so a tag
// with no name attribute matches and parses exactly as it always has.
const TODO_LIST_RE = /\[TODO_LIST(?::([a-z_]+))?(?:\s+name="([^"]*)")?\]([\s\S]*?)\[\/TODO_LIST\]/i;

// ---------------------------------------------------------------------------
// [LEARN] tag helpers
// ---------------------------------------------------------------------------

const LEARN_RE = /\[LEARN\]([\s\S]*?)\[\/LEARN\]/gi;

// ⚠️ The client no longer parses [LEARN] AT ALL. #915 finished what #890
// STEP 1 set out to do.
//
// This file used to hold LearnFact, TRAILING_TAG_RE, isTrailingLearnTag and
// parseLearnTags — a hand-kept mirror of kin-chat/learnTagPosition.ts — because
// the chip was built by parsing the tag out of the assistant's own text. That
// put the chip on the SAME channel the model writes, so a [LEARN] string in
// quoted or echoed content could render one. #901 narrowed that by requiring
// the trailing position on both sides; it did not remove the channel.
//
// Chips now come from server-authored fact_receipt events carrying real row ids
// (see LearnChips below). There is nothing left for the client to parse, so the
// mirror is gone rather than kept in sync forever — one fewer hand-copied pair,
// which is the same argument #916 makes about the others.
//
// stripLearnTags stays: the tag is still IN the streamed text and must not be
// shown to her.

function stripLearnTags(content: string): string {
  return content.replace(LEARN_RE, "").trim();
}

// naturalPhrase moved to src/lib/familyFactPhrase.ts in #888 so voice's receipt
// and this chip read identically from one implementation. Imported at the top
// of this file; do not reintroduce a local copy.

export function parseTodoList(content: string): { items: string[]; source: string; suggestedName: string | null } | null {
  const match = content.match(TODO_LIST_RE);
  if (!match) return null;
  const source = (match[1] || "chat").toLowerCase();
  // Copilot (#816): the name="..." capture is [^"]* so it can carry a
  // newline/tab if the model ever emits one — normalize internal whitespace
  // to a single space so the suggested name can never render as multi-line
  // text in the prefilled input or the confirmation toast.
  const rawName = match[2] ? match[2].trim().replace(/\s+/g, " ").slice(0, 100) : "";
  const suggestedName = rawName.length > 0 ? rawName : null;
  const block = match[3];
  const items = block
    .split("\n")
    .map((l) => l.trim())
    .map((l) =>
      l
        .replace(/^[-*•·]\s*/, "")
        .replace(/^\d+[.)]\s*/, "")
        .replace(/^\[\s*\]\s*/, "")
        .trim()
    )
    .filter((l) => l.length > 0 && l.length < 300);
  if (items.length === 0) return null;
  return { items, source, suggestedName };
}

export function stripTodoListTag(content: string): string {
  return content
    .replace(TODO_LIST_RE, "") // paired block first
    .replace(/\[\/?TODO_LIST(?::[a-z_]+)?(?:\s+name="[^"]*")?\]/gi, "") // orphan openers/closers
    .trim();
}

export function suppressUnclosedTodoList(content: string): string {
  const open = content.search(/\[TODO_LIST(?::[a-z_]+)?(?:\s+name="[^"]*")?\]/i);
  if (open === -1) return content;
  const hasClose = /\[\/TODO_LIST\]/i.test(content.slice(open));
  if (hasClose) return content; // complete block — let stripTodoListTag handle
  return content.slice(0, open).trimEnd();
}

/**
 * #680 — themed to-do lists get their own confirmation, naming exactly
 * where they landed. Deliberately NOT showSavedToMyKinToast (frozen, shared
 * by RECIPE/MEAL_PLAN/GROCERY_LIST too, and its copy is fixed per
 * destination type — it has no way to say "Back to School"). A separate,
 * new toast for the one case that needs to name a specific list.
 */
// #1028 — to-dos live in Tasks → Whenever now; their View-it lands there.
//
// #1877 — with a list id, it also asks WheneverLists to expand that one list.
// ⚠️ sessionStorage, NOT a `?open=` URL param: `TodayTab` strips `open`
// unconditionally and keeps a hand-maintained list of which tab owns which
// value, so a new param means editing the most fragile file in the repo. This
// path already existed for `kin_rtf_segment` and is the journey's own channel.
// WheneverLists CLEARS the key as it reads it, so it cannot re-fire on her next
// visit to Tasks.
function navigateToWheneverLists(listId?: string | null) {
  try {
    sessionStorage.setItem("kin_rtf_segment", "whenever");
    if (listId) sessionStorage.setItem(OPEN_TODO_LIST_KEY, listId);
  } catch { /* private mode */ }
  window.dispatchEvent(new CustomEvent("kin:navigate-tab", { detail: { tab: "tasks" } }));
}

/**
 * #1877 — she already saved this one.
 *
 * ⚠️ Says so plainly instead of reporting a fresh save. Confirming a write
 * that did not happen is house rule 16's lie in the other direction, and
 * "Saved!" over a no-op is what would make her tap a third time.
 */
/**
 * #1877 — the item texts already on a destination, for the duplicate check.
 *
 * ⚠️ A FAILED READ RETURNS [] AND THE SAVE PROCEEDS, deliberately. The three
 * states are "already there", "not there", and "could not find out" — and the
 * third must resolve towards saving, because the costs are not symmetric:
 *
 *   a wrong "already there"  -> her list silently never gets the items, which
 *                               is the exact complaint behind #1849/#1850
 *   a wrong "not there"      -> duplicate rows, annoying and recoverable in a
 *                               tap each now that #1851 gave her Delete
 *
 * So this guard removes a duplicate when it can prove one, and never blocks a
 * save when it cannot. It is an improvement, not a safety barrier.
 */
async function readExistingTodoTexts(
  userId: string,
  listId: string | null,
  includeLegacy: boolean
): Promise<string[]> {
  const texts: string[] = [];
  try {
    if (listId) {
      const { data, error } = await (supabase as any)
        .from("todo_list_items")
        .select("item")
        .eq("user_id", userId)
        .eq("list_id", listId);
      if (error) {
        console.warn("[SaveTodosButton] duplicate pre-check read failed; saving anyway", error);
        return [];
      }
      for (const r of (data || []) as Array<{ item: string }>) texts.push(r.item);
    }
    if (includeLegacy) {
      const { data, error } = await supabase
        .from("notes_todos")
        .select("item")
        .eq("user_id", userId);
      if (error) {
        console.warn("[SaveTodosButton] legacy duplicate pre-check read failed; saving anyway", error);
        return [];
      }
      for (const r of (data || []) as Array<{ item: string }>) texts.push(r.item);
    }
  } catch (e) {
    console.warn("[SaveTodosButton] duplicate pre-check threw; saving anyway", e);
    return [];
  }
  return texts;
}

function showAlreadySavedToast(listName: string | null, listId?: string | null) {
  toast.success(
    listName ? `Already on your ${listName} list 💜` : "Already on your To-Dos 💜",
    { action: { label: "View it", onClick: () => navigateToWheneverLists(listId) } }
  );
}

function showSavedToNamedListToast(listName: string, isNewList: boolean, listId?: string | null) {
  toast.success(isNewList ? `Saved to your new ${listName} list 💜` : `Added to your ${listName} list 💜`, {
    action: {
      label: "View it",
      onClick: () => navigateToWheneverLists(listId),
    },
  });
}

/**
 * #1877 — the saved pill's two labels.
 *
 * Exported constants rather than inline strings so the component and its tests
 * cannot drift, and so a copy change is one edit in one place.
 *
 * ⚠️ `SAVED_LINKED_LABEL` reuses "View it" — Kasandra's own word, already the
 * toast's action label for this exact journey (`showSavedToNamedListToast`).
 * A second phrase for one destination would be new copy; this is the existing
 * copy appearing in a second place.
 */
export const SAVED_LABEL = "Saved ✓";
export const SAVED_LINKED_LABEL = "Saved ✓ · View it";

type TodoSaveDestination =
  | { type: "new"; name: string }
  | { type: "existing"; listId: string; listName: string }
  | { type: "general" };

// Exported purely to enable direct unit testing (zero behavior change) —
// same rationale as parseTodoList/stripTodoListTag/suppressUnclosedTodoList
// above.
export const SaveTodosButton = ({
  items,
  source,
  suggestedName,
  messageId,
  savedListId: savedListIdFromRow = null,
}: {
  items: string[];
  source: string;
  suggestedName: string | null;
  /**
   * #1882 — the `messages` row this `[TODO_LIST]` came from. Optional: a
   * streaming message, an unproved insert, and a local-only bubble all
   * legitimately have none, and the save must work identically without it.
   */
  messageId?: string;
  /** #1877 — `messages.saved_list_id`, so the link survives a reload. */
  savedListId?: string | null;
}) => {
  const { user } = useAuth();
  // #1877 — the durable half, DERIVED rather than copied into state. `useState`
  // seeded from a prop reads it once on mount and then ignores it, so a history
  // load that re-renders this instance with a different row would be silently
  // ignored. The local value only ever ADDS a link this session; the row is the
  // source of truth whenever there is no local one.
  const [savedListIdLocal, setSavedListIdLocal] = useState<string | null>(null);
  const savedListId = savedListIdLocal ?? savedListIdFromRow;
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [existingLists, setExistingLists] = useState<{ id: string; name: string }[]>([]);
  const [destination, setDestination] = useState<TodoSaveDestination>(
    suggestedName ? { type: "new", name: suggestedName } : { type: "general" }
  );
  const [nameDraft, setNameDraft] = useState(suggestedName || "");

  // Only fetches existing lists when the richer picker will actually render
  // — the no-name path below never touches this, staying byte-identical to
  // the original component in both behavior and network calls.
  useEffect(() => {
    if (!user || !suggestedName) return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("todo_lists")
        .select("id, name")
        .eq("user_id", user.id)
        .order("created_at", { ascending: true });
      if (!cancelled && !error) setExistingLists(data || []);
    })();
    return () => {
      cancelled = true;
    };
  }, [user, suggestedName]);

  const handleSave = async () => {
    if (!user || saving || saved) return;
    // #1877 — ⚠️ THE DUPLICATE-SAVE GUARD, and the reason this issue was not a
    // small change. `saved` above is component state, so after a reload it is
    // false again and a second tap ran the whole insert: for a new list that
    // meant a SECOND `todo_lists` row with the same name, which nothing in the
    // app merges. `savedListId` comes back from the message ROW, so it survives
    // the reload the state does not — and with it present this NAVIGATES AND
    // WRITES NOTHING.
    //
    // #1881's content-keyed check still runs below and is still needed: it
    // covers the general-To-Dos destination, which has no list id to record,
    // and the window before the SQL is run. This one is the cheaper, stronger
    // guard where it applies — it never touches the database at all.
    if (savedListId) {
      navigateToWheneverLists(savedListId);
      return;
    }
    setSaving(true);
    try {
      if (destination.type === "general") {
        // #1028 — "general" now means the unified default To-Dos list, not
        // legacy notes_todos. If the default list can't be resolved or the
        // unified insert fails, fall back to the legacy write so her save
        // never breaks — the read bridge shows legacy rows under To-Dos
        // everywhere, so a fallback row is visible, not lost. Both paths
        // verify rows came back before confirming (house rule 16).
        const defaultList = await ensureDefaultTodoList(supabase, user.id);

        // #1877 — what is already on her To-Dos? Both stores, because the read
        // bridge shows legacy notes_todos rows under To-Dos too, so an item
        // that landed there on an earlier tap is on screen and must not be
        // written again.
        const alreadyThere = await readExistingTodoTexts(
          user.id,
          defaultList ? defaultList.id : null,
          true
        );
        const toWrite = itemsNotAlreadySaved(items, alreadyThere);
        if (toWrite.length === 0) {
          setSaved(true);
          showAlreadySavedToast(null);
          return;
        }

        let saved = false;
        if (defaultList) {
          const { data, error } = await (supabase as any)
            .from("todo_list_items")
            .insert(toWrite.map((item) => ({ user_id: user.id, list_id: defaultList.id, item, checked: false, source })))
            .select("id");
          saved = !error && Array.isArray(data) && data.length === toWrite.length;
          if (!saved) console.warn("[ChatMessages] unified general save failed; falling back to legacy", error);
        }
        if (!saved) {
          const { data, error } = await supabase
            .from("notes_todos")
            .insert(toWrite.map((item) => ({ user_id: user.id, item, checked: false, source })))
            .select("id");
          if (error || !data || data.length !== toWrite.length) {
            toast.error("Couldn't save the to-dos. Try again?");
            return;
          }
        }
        setSaved(true);
        logActivity("todo_saved", { count: toWrite.length, source, destination: "general" });
        // #1028 — deliberately NOT showSavedToMyKinToast (frozen; its copy
        // says "My Kin" and to-dos no longer live there).
        toast.success("Saved to your To-Dos 💜", {
          // #1877 — the general To-Dos destination has NO todo_lists row, so
          // there is no id to carry and nothing to expand. Unchanged behaviour,
          // spelled out because the named-list paths below now pass one.
          action: { label: "View it", onClick: () => navigateToWheneverLists() },
        });
        return;
      }

      let listId: string;
      let listName: string;
      let isNewList: boolean;

      if (destination.type === "new") {
        const name = nameDraft.trim();
        if (!name) {
          toast.error("Give the list a name first");
          return;
        }
        // #1877 — ⚠️ THIS IS THE REPORTED DEFECT. `saved` is component state,
        // so after a reload this button is unsaved again and a second tap used
        // to INSERT A SECOND `todo_lists` ROW WITH THE SAME NAME. Nothing in
        // the app merges two lists, so she was left to find and delete one.
        //
        // The fresh mount's list fetch already contains the list the first tap
        // created, so reusing it by name is both the fix and the natural
        // behaviour. Cost, stated: someone who genuinely wants two lists
        // called "Groceries" gets one.
        const existing = findListByName(existingLists, name);
        if (existing) {
          listId = existing.id;
          listName = existing.name;
          isNewList = false;
        } else {
          const { data, error } = await supabase
            .from("todo_lists")
            .insert({ user_id: user.id, name })
            .select("id, name")
            .single();
          if (error || !data) {
            toast.error("Couldn't save the to-dos. Try again?");
            return;
          }
          listId = data.id;
          listName = data.name;
          isNewList = true;
        }
      } else {
        listId = destination.listId;
        listName = destination.listName;
        isNewList = false;
      }

      // #1877 — only write what is not already on that list. On a repeat tap
      // this is empty and nothing is written at all.
      const alreadyOnList = await readExistingTodoTexts(user.id, listId, false);
      const toWrite = itemsNotAlreadySaved(items, alreadyOnList);
      if (toWrite.length === 0) {
        setSaved(true);
        // #1877 — the items are already there, so this message IS the one that
        // put them there (or an identical one). Record the link so the next tap
        // never reaches the database at all, and offer it either way.
        if (await recordSavedList(supabase as never, messageId, listId)) {
          setSavedListIdLocal(listId);
        }
        showAlreadySavedToast(listName, listId);
        return;
      }

      const { error: itemsError } = await supabase
        .from("todo_list_items")
        .insert(toWrite.map((item) => ({ user_id: user.id, list_id: listId, item, checked: false, source })));
      if (itemsError) {
        // Copilot (#816): the todo_lists insert above already landed by this
        // point for a NEW list — leaving it would show her an empty list she
        // never asked to see. Best-effort: its own failure doesn't change
        // the error she's shown (the save still failed either way).
        if (isNewList) {
          const { error: cleanupError } = await supabase.from("todo_lists").delete().eq("id", listId);
          if (cleanupError) {
            console.error("[SaveTodosButton] failed to clean up orphaned list", cleanupError);
          }
        }
        toast.error("Couldn't save the to-dos. Try again?");
        return;
      }
      setSaved(true);
      logActivity("todo_saved", {
        count: toWrite.length,
        source,
        destination: isNewList ? "new_list" : "existing_list",
      });
      // #1877 — the durable link, recorded AFTER the items are known to have
      // landed and BEFORE the toast, so the toast can tell the truth about what
      // it offers.
      //
      // ⚠️ A FAILED WRITE-BACK DOES NOT FAIL THE SAVE. Her to-dos are on the
      // list; what is missing is the bookmark. So the toast still appears, with
      // its "View it" action still working (navigation needs no stored row) —
      // only the message's pill stays un-linked, and a later tap falls through
      // to #1881's content-keyed check, which writes nothing either.
      if (await recordSavedList(supabase as never, messageId, listId)) {
        setSavedListIdLocal(listId);
      }
      showSavedToNamedListToast(listName, isNewList, listId);
    } finally {
      setSaving(false);
    }
  };

  // Backward-compatible path: no suggested name → the exact original
  // single-button behavior, unchanged. DONE WHEN: "A [TODO_LIST] tag with
  // no suggested name behaves exactly as it does today."
  if (!suggestedName) {
    return (
      <PillButton
        onClick={handleSave}
        disabled={saving}
        saved={saved}
        label="📝 Save to-dos to My Kin"
        savingLabel="Tucking it away..."
        // #1877 — the general To-Dos destination has no todo_lists row to point
        // at, so this pill is never a link. Through the constant anyway, so a
        // copy change cannot leave the two paths saying different things.
        savedLabel={SAVED_LABEL}
      />
    );
  }

  // #1877 — a saved message's pill. With a recorded list it is a LINK: tapping
  // opens Tasks → Whenever with that list expanded. Without one (the general
  // To-Dos destination, a message from before this shipped, or a write-back
  // that did not land) it stays the inert badge it has always been — the pill
  // never offers a journey it cannot complete.
  if (saved || savedListId) {
    return (
      <PillButton
        onClick={savedListId ? () => navigateToWheneverLists(savedListId) : () => {}}
        disabled={false}
        saved
        savedClickable={!!savedListId}
        label="📝 Save to-dos to My Kin"
        savingLabel="Tucking it away..."
        // #1877 — a LINKED pill says so; an unlinked one does not.
        //
        // ⚠️ The two labels are not cosmetic variants of each other. A pill that
        // reads "View it" and goes nowhere is worse than one that just says
        // "Saved ✓", so the affordance is tied to `savedListId` — the same
        // condition that makes the button clickable at all. Never set one
        // without the other.
        savedLabel={savedListId ? SAVED_LINKED_LABEL : SAVED_LABEL}
      />
    );
  }

  const saveLabel =
    destination.type === "new"
      ? "📝 Save as new list"
      : destination.type === "existing"
      ? `📝 Save to "${destination.listName}"`
      : "📝 Save to general to-dos";

  return (
    <div className="flex items-center gap-1.5 flex-wrap" style={{ minWidth: 0 }}>
      {destination.type === "new" && (
        <input
          type="text"
          value={nameDraft}
          onChange={(e) => setNameDraft(e.target.value)}
          placeholder="List name"
          aria-label="List name"
          maxLength={100}
          style={{
            fontSize: "clamp(12px, 2.8vw, 13px)",
            padding: "6px 10px",
            borderRadius: 999,
            border: "1px solid #C8C8F4",
            color: "#1C1A3E",
            minWidth: 90,
            maxWidth: 160,
          }}
        />
      )}
      <PillButton
        onClick={handleSave}
        disabled={saving || (destination.type === "new" && !nameDraft.trim())}
        saved={false}
        label={saveLabel}
        savingLabel="Tucking it away..."
        // `saved={false}` here always — the saved states are the two early
        // returns above — so this never renders. Through the constant so it
        // cannot become the odd one out.
        savedLabel={SAVED_LABEL}
      />
      <DropdownMenu>
        <DropdownMenuTrigger
          aria-label="Choose a different list to save to"
          className="flex-shrink-0 flex items-center justify-center"
          style={{ width: 26, height: 26, color: "#94A3B8", fontSize: 12 }}
        >
          ▾
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="z-[70]">
          <DropdownMenuItem
            onClick={() => {
              // Copilot (#816): also reset the editable draft, not just
              // destination — otherwise a name she edited before switching
              // away and back shows here instead of the suggestion this
              // menu item names.
              setDestination({ type: "new", name: suggestedName });
              setNameDraft(suggestedName);
            }}
          >
            New list: {suggestedName}
          </DropdownMenuItem>
          {existingLists.map((l) => (
            <DropdownMenuItem
              key={l.id}
              onClick={() => setDestination({ type: "existing", listId: l.id, listName: l.name })}
            >
              {l.name}
            </DropdownMenuItem>
          ))}
          <DropdownMenuItem onClick={() => setDestination({ type: "general" })}>General to-dos</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Family-memory confirmation chips ([LEARN] tags)
// ---------------------------------------------------------------------------

const LearnChip = ({ fact, onUndo }: { fact: LearnReceipt; onUndo: () => void }) => {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;

  const handleUndo = () => {
    setVisible(false);
    onUndo();
  };

  return (
    <div
      className="flex items-center gap-1.5 rounded-full flex-shrink-0"
      style={{
        fontSize: "clamp(11px, 2.6vw, 12px)",
        padding: "5px 10px",
        backgroundColor: "#FFFFFF",
        border: "1px solid #C8C8F4",
        color: "#5050C8",
        marginTop: 4,
      }}
    >
      <span>💜 Got it, I&apos;ll remember {naturalPhrase(fact)}.</span>
      <button
        onClick={handleUndo}
        style={{ color: "#5050C8", opacity: 0.6, fontWeight: 600, marginLeft: 4 }}
      >
        Undo
      </button>
    </div>
  );
};

/**
 * #1896 — "Which row is yours?" on a snapped sign-up sheet.
 *
 * Multi-select, with the rows that matched her first name or a kid's full name
 * pre-ticked. Rendered in the thread like a tool-event card rather than sent as
 * a model turn: the model never sees these rows, and the answer goes straight
 * back to signup-sheet-parse.
 *
 * ⚠️ NOTHING IS FILED until she taps Done. The function returns the rows and
 * writes nothing on the asking pass, so a sheet she never answers leaves no
 * trace at all.
 */
const SignupRowPicker = ({
  picker,
  onDone,
}: {
  picker: SignupPicker;
  onDone?: (rowIndexes: number[]) => void;
}) => {
  // Oct 27 2026, first real sheet: 17 chips plus a Done button that ended up
  // below the fold, and nothing on screen after tapping it. Now:
  //   • Kin found a likely row  → show just that, "Yes, file it" / "Not mine".
  //   • otherwise (or Not mine) → the signed rows; ONE tap on a row files it.
  // No Done button anywhere. Every tap is the whole answer.
  const matched = picker.rows.filter((r) => (picker.matchedRows ?? []).includes(r.index));
  const [mode, setMode] = useState<"confirm" | "list">(matched.length > 0 ? "confirm" : "list");
  const [picked, setPicked] = useState<number | "none" | "confirmed" | null>(null);
  const done = picker.answered === true || picked !== null;

  const choose = (rowIndexes: number[], mark: number | "none" | "confirmed") => {
    if (done) return;
    setPicked(mark);
    onDone?.(rowIndexes);
  };

  const rowStyle = (on: boolean): React.CSSProperties => ({
    width: "100%", minHeight: 48, padding: "10px 14px", borderRadius: 14,
    border: `1.5px solid ${on ? "#5050C8" : "#E0DEEE"}`,
    background: on ? "#5050C8" : "#fff",
    color: on ? "#fff" : "#1C1A3E",
    fontSize: 14, fontWeight: 600, textAlign: "left",
    cursor: done ? "default" : "pointer", opacity: done && !on ? 0.5 : 1,
  });
  const primary: React.CSSProperties = {
    minHeight: 44, padding: "0 18px", borderRadius: 12, border: "none",
    background: "#5050C8", color: "#fff", fontSize: 14, fontWeight: 700,
    cursor: done ? "default" : "pointer", opacity: done && picked !== "confirmed" ? 0.5 : 1,
  };
  const secondary: React.CSSProperties = {
    minHeight: 44, padding: "0 16px", borderRadius: 12,
    border: "1.5px solid #C8C8F4", background: "#fff", color: "#5050C8",
    fontSize: 14, fontWeight: 600, cursor: done ? "default" : "pointer",
    opacity: done ? 0.5 : 1,
  };

  if (mode === "confirm") {
    return (
      <div data-testid="signup-row-picker" data-mode="confirm" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
        {matched.map((row) => (
          <div key={row.index} className="font-body" style={{ ...rowStyle(false), cursor: "default", opacity: 1 }}>
            {signupRowChipLabel(row)}
          </div>
        ))}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button"
            disabled={done}
            data-testid="signup-confirm-yes"
            onClick={() => choose(matched.map((r) => r.index), "confirmed")}
            className="font-body"
            style={primary}
          >
            Yes, file it
          </button>
          <button
            type="button"
            disabled={done}
            data-testid="signup-confirm-no"
            onClick={() => { if (!done) setMode("list"); }}
            className="font-body"
            style={secondary}
          >
            Not mine
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="signup-row-picker" data-mode="list" style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 8 }}>
      {matched.length > 0 && (
        <p className="font-body" style={{ margin: 0, fontSize: 13, color: "#54525C" }}>
          Tap your row and Kin files it.
        </p>
      )}
      {picker.rows.map((row) => (
        <button
          key={row.index}
          type="button"
          disabled={done}
          onClick={() => choose([row.index], row.index)}
          className="font-body"
          style={rowStyle(picked === row.index)}
        >
          {signupRowChipLabel(row)}
        </button>
      ))}
      <button
        type="button"
        disabled={done}
        onClick={() => choose([], "none")}
        className="font-body"
        style={{ ...secondary, alignSelf: "flex-start" }}
      >
        None of these
      </button>
    </div>
  );
};

const LearnChips = ({ msg }: { msg: Message }) => {
  const { user } = useAuth();

  // ⚠️ Reads the SERVER's receipts, never the message text. #915.
  //
  // This used to be `parseLearnTags(msg.content)`. Two problems, and the second
  // is the one #890 STEP 1 was actually about:
  //
  //   1. The tag carries no row id, so Undo matched on category+key+subject.
  //      That is not a row selector — the six multi-valued keys (#747) keep
  //      several live rows per that tuple — so undoing Carson's SHELLFISH
  //      allergy archived his PEANUT allergy too.
  //   2. Message text is the channel the MODEL writes. A [LEARN] string in
  //      quoted or echoed content could render a chip at all. Position
  //      enforcement (#901) narrowed that; sourcing the chip from a
  //      server-authored event closes it, because the client can no longer act
  //      on anything this server did not send on the dedicated field.
  //
  // Nothing was lost moving off the text: the tag is stripped before the
  // message is persisted (see the assistant insert in Index.tsx), so chips were
  // already live-session only.
  const facts = msg.learnReceipts ?? [];
  // Silent keys (gender, partner relation) are stored and used but never
  // drawn — Kasandra's call, see SILENT_LEARN_KEYS. Filtered BEFORE the
  // early return so a message whose only capture is silent renders nothing.
  // (`facts` stays the server's list verbatim: two #915/#890 guards pin that
  // exact line as proof chips never come from message text.)
  const shown = facts.filter((f) => !isSilentLearnKey(f.key));
  if (shown.length === 0) return null;

  const handleUndo = (fact: LearnReceipt) => {
    if (!user) return;
    void (async () => {
      try {
        // Archive THIS row. The id is the whole reason this component now reads
        // receipts instead of text.
        const { data, error } = await (supabase as any)
          .from("family_context")
          .update({ archived: true })
          .eq("id", fact.id)
          // Belt and braces: the id came from this user's own stream, but RLS
          // is the thing that must hold, not the provenance of the id.
          .eq("user_id", user.id)
          .select("id");
        if (error) {
          console.warn("[family-memory] undo update failed:", error.message);
          toast.error("Couldn't undo that — you can remove it in My Kin.");
          return;
        }
        // House rule 16: an RLS-filtered UPDATE matching ZERO rows returns 200
        // with error === null. Reading only the error cannot see it, and the
        // chip stays on screen either way — so a failed undo was silent.
        if (!Array.isArray(data) || data.length === 0) {
          console.warn("[family-memory] undo matched no rows:", fact.id);
          toast.error("Couldn't undo that — you can remove it in My Kin.");
          return;
        }
        console.log("[family-memory] undid learn fact:", fact.id, fact.category, fact.key);
        toast("Okay, forgotten.");
      } catch (e) {
        console.warn("[family-memory] undo threw:", e instanceof Error ? e.message : String(e));
        toast.error("Couldn't undo that — you can remove it in My Kin.");
      }
    })();
  };

  return (
    <div className="flex flex-col gap-0" style={{ marginTop: 2 }}>
      {shown.map((fact) => (
        <LearnChip key={fact.id} fact={fact} onUndo={() => handleUndo(fact)} />
      ))}
    </div>
  );
};

/** Per-message link refs keyed by message index (recreated each render is fine for short chat). */
const MessageActions = ({ msg }: { msg: Message }) => {
  const linkRef = useRef<LinkRef>({});
  // ⚠️ #1900 — the draft block goes from the COPY text as well. This is what
  // the copy button hands to her clipboard, and a routine she pastes into a
  // text to her husband must not have a JSON blob stapled to the end of it.
  const cleanContent = stripRoutineDraft(
    stripLearnTags(stripTodoListTag(stripMealPlanTags(msg.content))),
  );
  const mealType = detectMealPlanType(msg.content);
  // Detect grocery on RAW content so the structured [GROCERY_LIST] block
  // (which stripMealPlanTags removes from view) is still found.
  const hasGrocery = hasGroceryList(msg.content);
  const showSaveButton = hasGrocery || mealType === "meal_plan" || mealType === "recipe";
  const todoList = parseTodoList(msg.content);

  return (
    <div
      className="flex items-center gap-2 mt-1.5 w-full box-border"
      style={{ minWidth: 0, flexWrap: "wrap", paddingBottom: "8px" }}
    >
      <CopyButton text={cleanContent} />
      {hasGrocery && (
        <CopyButton text={parseGroceryItems(msg.content).join("\n")} label="🛒 Copy grocery list" />
      )}
      {showSaveButton && (
        <SaveToMyKinButton
          messageContent={msg.content}
          rawContent={msg.content}
          mealType={mealType}
          hasGrocery={hasGrocery}
          linkRef={linkRef}
        />
      )}
      {todoList && !showSaveButton && (
        <SaveTodosButton
          items={todoList.items}
          source={todoList.source}
          suggestedName={todoList.suggestedName}
          // #1882 / #1877 — the message's own identity and the list it already
          // saved to. Both optional; without them the button behaves exactly as
          // it did before, which is what a message rendered mid-stream gets.
          messageId={msg.id}
          savedListId={msg.savedListId ?? null}
        />
      )}
      <MessageFeedback messageContent={cleanContent} />
      {hasGrocery && (
        <div className="w-full" style={{ marginTop: 8 }}>
          <KrogerPricing
            compact
            directItems={parseGroceryItems(msg.content)}
          />
        </div>
      )}
    </div>
  );
};


export interface ChatMessagesHandle {
  /** Called by parent when the user sends a new message — forces re-pin to bottom even if she had scrolled up. */
  resetScrollLock: () => void;
}

const ChatMessages = forwardRef<ChatMessagesHandle, ChatMessagesProps>(({ messages, isLoading, selectedImageUrl, onImageSelect, onAskAboutPhoto, onOpenRenoTracker, onSendMessage, onSignupRowsChosen, keyboardOpen }, ref) => {
  const bottomRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const userScrolledUpRef = useRef(false);
  const programmaticScrollRef = useRef(false);

  // Wrap every scrollIntoView so the scroll listener can distinguish our own
  // programmatic scrolls from real user gestures and not falsely trip the
  // userScrolledUp guard mid-animation.
  const programmaticScroll = (opts: ScrollIntoViewOptions) => {
    programmaticScrollRef.current = true;
    bottomRef.current?.scrollIntoView(opts);
    // Two nested rAFs — some browsers fire scroll events one frame after
    // the scrollIntoView call commits.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        programmaticScrollRef.current = false;
      });
    });
  };

  useImperativeHandle(ref, () => ({
    resetScrollLock: () => {
      userScrolledUpRef.current = false;
    },
  }), []);

  // Walk up from containerRef to find the nearest ancestor that actually
  // scrolls. The chat scroller is the <main> in Index.tsx, NOT window.
  const findScrollContainer = (el: HTMLElement | null): HTMLElement | Window => {
    let cur = el?.parentElement;
    while (cur) {
      const style = window.getComputedStyle(cur);
      if (style.overflowY === "auto" || style.overflowY === "scroll") {
        return cur;
      }
      cur = cur.parentElement;
    }
    return window;
  };

  // Detect when the user has manually scrolled up so we don't yank them
  // back to the bottom on every streaming chunk.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const scrollEl = findScrollContainer(container);

    const handleScroll = () => {
      if (programmaticScrollRef.current) return; // ignore our own scrolls
      let rawDist: number;
      if (scrollEl === window) {
        rawDist =
          document.documentElement.scrollHeight -
          window.scrollY -
          window.innerHeight;
      } else {
        const el = scrollEl as HTMLElement;
        rawDist = el.scrollHeight - el.scrollTop - el.clientHeight;
      }
      userScrolledUpRef.current = rawDist > 100;
    };

    scrollEl.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollEl.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (isLoading) return;
    const scrollEl = findScrollContainer(containerRef.current);
    const log = (label: string) => {
      if (!scrollEl) return;
      const el = scrollEl as HTMLElement;
      console.log(`[scroll ${label}]`, {
        scrollTop: el.scrollTop,
        scrollHeight: el.scrollHeight,
        clientHeight: el.clientHeight,
        distFromBottom: el.scrollHeight - el.scrollTop - el.clientHeight,
      });
    };
    log("before-scroll");
    requestAnimationFrame(() => requestAnimationFrame(() => {
      programmaticScroll({ behavior: "auto", block: "end" });
      requestAnimationFrame(() => requestAnimationFrame(() => {
        log("after-scroll-2raf");
        setTimeout(() => log("after-500ms"), 500);
        setTimeout(() => log("after-1500ms"), 1500);
      }));
    }));
  }, [messages, isLoading]);

  // When the iOS keyboard opens, re-pin to keep the latest message visible
  // above the input bar. VV race condition is now handled by
  // useVisualViewportBottom in ChatInput, so no rAF delay needed here.
  useEffect(() => {
    if (keyboardOpen) {
      programmaticScroll({ behavior: "auto", block: "end" });
    }
  }, [keyboardOpen]);

  // Re-pin to bottom on ANY height change (streaming chunks, late image loads,
  // Kroger panel results) — unless the user has scrolled up to read history.
  // Debounced via cancelable rAF so multi-pass layouts (KrogerPricing settling
  // over several frames) coalesce into ONE final scroll after the DOM settles.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let pendingScrollFrame: number | null = null;

    const ro = new ResizeObserver(() => {
      if (userScrolledUpRef.current) return;

      // Cancel any pending scroll — only the latest tick should land
      if (pendingScrollFrame !== null) {
        cancelAnimationFrame(pendingScrollFrame);
      }

      // Double rAF — wait until after layout fully commits
      pendingScrollFrame = requestAnimationFrame(() => {
        pendingScrollFrame = requestAnimationFrame(() => {
          programmaticScroll({ behavior: "auto", block: "end" });
          pendingScrollFrame = null;
        });
      });
    });

    ro.observe(container);
    const scrollEl = findScrollContainer(container);
    if (scrollEl instanceof HTMLElement && scrollEl !== container) {
      ro.observe(scrollEl);
    }
    return () => {
      ro.disconnect();
      if (pendingScrollFrame !== null) cancelAnimationFrame(pendingScrollFrame);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="flex flex-col gap-4 px-4 py-4 max-w-2xl mx-auto w-full"
    >
      {messages.map((msg, i) =>
        msg.signupPending ? (
          <TypingIndicator
            key={i}
            phrases={msg.signupPending === "filing" ? SIGNUP_FILING_PHRASES : SIGNUP_READING_PHRASES}
          />
        ) : msg.role === "user" ? (
          <div key={i} className="flex justify-end animate-fade-slide-in">
            <div
              className="max-w-[78%] px-4 py-3 text-sm font-body text-primary-foreground leading-relaxed
                bg-gradient-to-br from-kin-periwinkle to-kin-charcoal
                rounded-[18px] rounded-br-[4px]"
            >
              {msg.userImages && msg.userImages.length > 1 ? (
                // 2+ photos: compact 3-column square grid, capped so a full
                // 8-photo message doesn't dominate the bubble.
                <div className="grid grid-cols-3 gap-1 mb-2 max-w-[240px]">
                  {msg.userImages.map((src, idx) => (
                    <img
                      key={idx}
                      src={src}
                      alt={`Uploaded photo ${idx + 1}`}
                      className="w-full aspect-square object-cover rounded-[8px]"
                    />
                  ))}
                </div>
              ) : (msg.userImages?.[0] || msg.userImage) ? (
                // 1 photo (new or older messages) renders exactly as before.
                <img
                  src={msg.userImages?.[0] || msg.userImage}
                  alt="Uploaded photo"
                  className="max-w-[220px] w-full rounded-[10px] mb-2"
                />
              ) : null}
              {stripMealPlanRunTag(msg.content)}
            </div>
          </div>
        ) : (
          <div key={i} className="flex items-start gap-2.5 animate-fade-slide-in">
            <KinAvatar size="sm" variant="light" />
            <div className="flex flex-col w-full box-border overflow-x-hidden min-w-0" style={{ maxWidth: "calc(100% - 50px)" }}>
              <div
                className="py-1 text-base font-body text-foreground leading-relaxed"
              >
              {(() => {
                const scheduleEvents = parseScheduleJSON(msg.content);
                const afterSchedule = scheduleEvents ? stripScheduleJSON(msg.content) : msg.content;
                const afterSuppress = suppressUnclosedTodoList(afterSchedule);
                const afterTags = stripMealPlanTags(afterSuppress);
                const afterTodo = stripTodoListTag(afterTags);
                const afterLearn = stripLearnTags(afterTodo);
                // #1434 — the four [OPEN_*] modal markers. This half is what
                // fixes rows ALREADY carrying one: they were persisted before
                // the Index.tsx strip was corrected, and nothing here removed
                // them, so a reload showed the raw marker as literal text. A
                // render-side strip clears every existing row on deploy, which
                // is why this needs no SQL and no backfill.
                //
                // ⚠️ BUDGET_BUTTON_TAG is stripped separately and is NOT one of
                // them — it renders a button she taps, just below. Removing it
                // would delete the button, not a stray marker.
                // ⚠️ #1900 — stripRoutineDraft here too, and this is the half
                // that matters for rows ALREADY stored. The payload is JSON,
                // so a row persisted before this deploy renders a brace soup
                // in her chat rather than a stray marker. A render-side strip
                // clears every existing row with no SQL and no backfill, the
                // same reasoning #1434 used above.
                const stripped = stripRoutineDraft(
                  stripModalTags(afterLearn).split(BUDGET_BUTTON_TAG).join(""),
                );
                // #1998 — lift the Pro line out of the text only when the
                // function flagged it AND it is really there, so the words are
                // never lost and never shown twice.
                const proLine = msg.signupProUpsell === true && stripped.trimEnd().endsWith(SIGNUP_PRO_LINE);
                const cleanContent = proLine
                  ? stripped.trimEnd().slice(0, -SIGNUP_PRO_LINE.length).trimEnd()
                  : stripped;
                return (
                  <>
                    {renderContent(stripMealPlanRunTag(cleanContent))}
                    {proLine && (
                      <div className="mt-2" data-testid="signup-pro-link">
                        <ProUpsellLink label={SIGNUP_PRO_LINE} fontSize={15} />
                      </div>
                    )}
                    {msg.content.includes(BUDGET_BUTTON_TAG) && onOpenRenoTracker && (
                      <button
                        onClick={onOpenRenoTracker}
                        className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-body font-semibold text-sm transition-colors"
                        style={{ backgroundColor: "#5050C8", color: "#fff" }}
                      >
                        📋 Open Project Budget Tracker
                      </button>
                    )}
                    {scheduleEvents && (
                      <SchedulePreview events={scheduleEvents} followUpText="yes" onSendMessage={onSendMessage} />
                    )}
                    {msg.images && msg.images.length > 0 && (
                      <ImageGrid
                        images={msg.images}
                        selectedImageUrl={selectedImageUrl}
                        onImageSelect={onImageSelect}
                        onAskAboutPhoto={onAskAboutPhoto}
                      />
                    )}
                  </>
                );
              })()}
              </div>
              <MessageActions msg={msg} />
              <LearnChips msg={msg} />
              {msg.signupPicker && (
                <SignupRowPicker
                  picker={msg.signupPicker}
                  onDone={(rows) => onSignupRowsChosen?.(i, rows)}
                />
              )}
            </div>
          </div>
        )
      )}
      {isLoading && <TypingIndicator />}
      <div
        ref={bottomRef}
        aria-hidden
        data-chat-bottom-anchor
        style={{
          // Cosmetic spacer only — flexbox column in Index.tsx positions ChatInput,
          // so no large offset is needed to clear the input bar.
          height: 12,
          flexShrink: 0,
        }}
      />
    </div>
  );
});

ChatMessages.displayName = "ChatMessages";

export default ChatMessages;
